import { useMemo, useState, type FC } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal,
  Show
} from "shared/components";
import { Loader } from "shared/components/Loader";
import { cx } from "shared/utils";
import { validateEmail } from "shared/utils/valid-email";
import { getErrorMessage } from "utils/error";
import {
  getOwnerReportSettings,
  QUERY_OWNER_REPORT_SETTINGS
} from "api/owner-report-settings";
import {
  createOwnerReportShare,
  getOwnerReportShares,
  MUTATION_OWNER_REPORT_SHARE_CREATE,
  MUTATION_OWNER_REPORT_SHARE_DELETE,
  QUERY_OWNER_REPORT_SHARES,
  revokeOwnerReportShare,
  type OwnerReportShare,
  type OwnerSharePayload
} from "api/owner-report-shares";
import { ConfirmDialog } from "shared/components/modals/ConfirmDialog";
import { ShareTab } from "./ShareTab";
import { SharedLinksTab } from "./SharedLinksTab";
import { PreviewModal } from "./PreviewModal";

type Tab = "share" | "links";

const commitDraft = (emails: string[], draft: string): string[] => {
  const pending = draft.trim();
  if (!pending) return emails;
  if (emails.some((email) => email.toLowerCase() === pending.toLowerCase()))
    return emails;
  return [...emails, pending];
};

/**
 * Share report. Two tabs over one job: send this application's sanitized
 * screening summary outside the company — to an owner, a realtor, whoever the
 * partner needs — and manage the links already out.
 *
 * A share is one link however many people it goes to, which is why the Shared
 * links tab lists shares rather than recipients and offers one Revoke per row.
 */
export const ShareWithOwnerDialog: FC<{
  opened: boolean;
  onClose: () => void;
  applicationId: string | number;
  subtitle?: string;
}> = ({ opened, onClose, applicationId, subtitle }) => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("share");
  const [emails, setEmails] = useState<string[]>([]);
  const [draft, setDraft] = useState("");
  const [message, setMessage] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [revokingId, setRevokingId] = useState<number | null>(null);
  // Revoking cannot be undone — a recipient's link simply stops working — so it
  // asks first, as the ticket requires.
  const [pendingRevoke, setPendingRevoke] = useState<OwnerReportShare | null>(
    null
  );

  const {
    data: settings,
    isError: settingsFailed,
    isPending: settingsPending
  } = useQuery({
    queryKey: QUERY_OWNER_REPORT_SETTINGS,
    queryFn: getOwnerReportSettings,
    enabled: opened
  });

  const { data: shares = [], isError: sharesFailed } = useQuery({
    queryKey: QUERY_OWNER_REPORT_SHARES(applicationId),
    queryFn: () => getOwnerReportShares(applicationId),
    enabled: opened
  });

  // The draft counts: a partner who typed an address and went straight for Send
  // meant to send to it.
  const committed = useMemo(() => commitDraft(emails, draft), [emails, draft]);
  const canSubmit =
    committed.length > 0 && committed.every((email) => validateEmail(email));

  const activeCount = shares.filter((share) => share.state === "active").length;

  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: QUERY_OWNER_REPORT_SHARES(applicationId)
    });

  const share = useMutation({
    mutationKey: MUTATION_OWNER_REPORT_SHARE_CREATE,
    mutationFn: (payload: OwnerSharePayload) =>
      createOwnerReportShare(applicationId, payload),
    onError: (error: unknown) => toast.error(getErrorMessage(error))
  });

  const revoke = useMutation({
    mutationKey: MUTATION_OWNER_REPORT_SHARE_DELETE,
    mutationFn: (shareId: number) =>
      revokeOwnerReportShare(applicationId, shareId),
    onSuccess: () => {
      toast.success("Link revoked");
      refresh();
    },
    onError: (error: unknown) => toast.error(getErrorMessage(error)),
    onSettled: () => setRevokingId(null)
  });

  const resetForm = () => {
    setEmails([]);
    setDraft("");
    setMessage("");
  };

  // Only somebody who has not started their application is left out now. A check
  // still running costs that section and nothing else, so the old "share again
  // once their reports finish" no longer describes why anyone is missing.
  const warnAboutExcluded = (excluded: string[]) => {
    if (excluded.length === 0) return;
    toast.warning(
      `Not on the report, because they have not started their application: ${excluded.join(", ")}.`
    );
  };

  // Carries the recipients across to the Share tab rather than sharing behind the
  // partner's back: re-sending is a new link to real people, and the message and
  // the recipient list are both theirs to confirm first.
  const handleReshare = (stale: OwnerReportShare) => {
    setEmails(stale.emails);
    setDraft("");
    setTab("share");
  };

  const handleSend = () => {
    if (!canSubmit) return;
    share.mutate(
      { emails: committed, message: message || undefined, notify: true },
      {
        onSuccess: (result) => {
          toast.success(
            committed.length === 1
              ? "Report sent"
              : `Report sent to ${committed.length} recipients`
          );
          warnAboutExcluded(result.excluded_people);
          resetForm();
          refresh();
          // Straight to the list, so the partner sees the link they just made
          // rather than an empty form.
          setTab("links");
        }
      }
    );
  };

  const handleCopyLink = () => {
    if (!canSubmit) return;
    share.mutate(
      { emails: committed, message: message || undefined, notify: false },
      {
        onSuccess: (result) => {
          // The share is already created by the time this runs, so a rejected
          // clipboard write (Safari refuses one after an awaited request) must
          // not read as success — the partner would paste whatever was there
          // before, or press Copy link again and mint a second live link.
          navigator.clipboard
            .writeText(result.share.url ?? "")
            .then(() => toast.success("Link copied. Nobody was emailed."))
            .catch(() =>
              toast.warning(
                "Link created, but could not be copied. Copy it from Shared links."
              )
            );
          warnAboutExcluded(result.excluded_people);
          resetForm();
          refresh();
          setTab("links");
        }
      }
    );
  };

  const handleClose = () => {
    resetForm();
    setTab("share");
    onClose();
  };

  const isSending = share.isPending && share.variables?.notify !== false;
  const isCopying = share.isPending && share.variables?.notify === false;

  return (
    <>
      {/* The panel is a flex column that owns its own height, so the body
          scrolls and the actions underneath it do not. It used to scroll as one
          piece behind a sticky header: on a short viewport that put Preview,
          Copy link and Send report below the fold, and the dialog opened
          showing no way to act on it. */}
      <Modal
        opened={opened}
        onClose={handleClose}
        panelClassName="flex max-h-[90vh] w-full max-w-[660px] flex-col overflow-hidden px-6 pb-6"
      >
        <div className="shrink-0 bg-background-1000 pt-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col">
              <h2 className="text-lg font-bold text-content-1000">
                Share report
              </h2>
              <Show if={Boolean(subtitle)}>
                <p className="text-sm text-content-400">{subtitle}</p>
              </Show>
            </div>
            <IconButton
              HugeIcon={Cancel01Icon}
              variant={IconButtonVariant.TEXT_BASE}
              size={IconButtonSize.SM}
              onClick={handleClose}
              aria-label="Close"
            />
          </div>

          <div className="mt-4 flex gap-6 border-b border-alpha-100">
            <button
              type="button"
              onClick={() => setTab("share")}
              className={cx(
                "-mb-px border-b-2 pb-3 text-sm font-medium transition-colors",
                tab === "share"
                  ? "border-primary-500 text-primary-500"
                  : "border-transparent text-content-500 hover:text-content-1000"
              )}
            >
              Share
            </button>
            <button
              type="button"
              onClick={() => setTab("links")}
              className={cx(
                "-mb-px flex items-center gap-2 border-b-2 pb-3 text-sm font-medium transition-colors",
                tab === "links"
                  ? "border-primary-500 text-primary-500"
                  : "border-transparent text-content-500 hover:text-content-1000"
              )}
            >
              Shared links
              <Show if={activeCount > 0}>
                <span className="rounded-full bg-alpha-80 px-1.5 text-xs text-content-500">
                  {activeCount}
                </span>
              </Show>
            </button>
          </div>
        </div>

        {/* min-h-0 is what lets this shrink inside the flex column; without it
            a flex child refuses to go below its content height and the panel
            grows past max-h instead of scrolling. */}
        <div className="flex min-h-0 flex-1 flex-col">
          <Show if={settingsPending && !settingsFailed}>
            <div className="flex justify-center py-16">
              <Loader />
            </div>
          </Show>

          <Show if={settingsFailed}>
            <p className="py-16 text-center text-sm text-content-400">
              Could not load what recipients will see. Close this and try again.
            </p>
          </Show>

          {settings && tab === "share" ? (
            <ShareTab
              settings={settings}
              emails={emails}
              draft={draft}
              message={message}
              isSending={isSending}
              isCopying={isCopying}
              canSubmit={canSubmit}
              onEmailsChange={setEmails}
              onDraftChange={setDraft}
              onMessageChange={setMessage}
              onPreview={() => setShowPreview(true)}
              onCopyLink={handleCopyLink}
              onSend={handleSend}
            />
          ) : null}

          {settings && tab === "links" ? (
            <SharedLinksTab
              failed={sharesFailed}
              shares={shares}
              revokingId={revokingId}
              onRevoke={(share) => setPendingRevoke(share)}
              onReshare={handleReshare}
            />
          ) : null}
        </div>
      </Modal>

      <ConfirmDialog
        opened={Boolean(pendingRevoke)}
        onClose={() => setPendingRevoke(null)}
        onConfirm={() => {
          if (!pendingRevoke) return;
          setRevokingId(pendingRevoke.id);
          revoke.mutate(pendingRevoke.id);
          setPendingRevoke(null);
        }}
        title="Revoke this link?"
        description={`${pendingRevoke?.emails.join(", ") ?? ""} will no longer be able to open the report. This can't be undone.`}
        confirmButton={{ label: "Revoke" }}
        zIndex={60}
      />

      <PreviewModal
        opened={showPreview}
        onClose={() => setShowPreview(false)}
        applicationId={applicationId}
        message={message}
      />
    </>
  );
};
